import { selectItemById } from '@data/selectors/itemSelectors';
import { useSelector } from 'react-redux';
import { Route, Routes, useNavigate, useParams } from 'react-router-dom';
import { useCallback, useEffect, useState } from 'react';
import DeliveryReceiptView from './DeliveryReceiptView';
import axios, { CancelTokenSource } from 'axios';
import { FailedImage, UploadResult } from '@shared/types';
import DefaultSpinner from '@components/ui/DefaultSpinner';
import { Flex } from '@kvdbil/components';
import { imageCompression } from '@utils/imageCompression';
import { Item } from '@shared/interfaces';
import { log } from '@shared/bugsnag';
import { generateReceipt } from '@api/GenerateReceipt.ts';
import { GenerationStatus } from './types.ts';
import GeneratedReceiptView from './GeneratedReceiptView.tsx';
import { fetchDeliveryReceiptThunk } from '@data/actions/deliverReceiptActions.ts';
import { useAppDispatch } from '@hooks/reduxHooks.ts';
import { deliveryReceiptSelector } from '@data/selectors/deliveryReceiptSelectors.ts';
import { resetDeliveryReceiptDocument } from '@data/slices/deliveryReceiptDocumentSlice.ts';
import { resetDeliveryReceipt } from '@data/slices/deliveryReceiptSlice.ts';
import ItemLayout from '../Item/components/ItemLayout.tsx';
import { useTranslation } from 'react-i18next';
import { fetchItemsThunk } from '@data/actions/itemsActions.ts';

const handleGenerateReceipt = async ({
  file,
  item,
  source,
}: {
  file: File;
  item: Item;
  source: CancelTokenSource;
}): Promise<UploadResult> => {
  let compressedFile: File | null;

  const getErrorResp = (message?: string): FailedImage => ({
    status: 'FAILED',
    name: file.name,
    size: file.size,
    type: file.type,
    error: new Error(message ? message : 'Image compression failed'),
  });

  try {
    compressedFile = await imageCompression(file);
  } catch (error: unknown) {
    return getErrorResp(error instanceof Error ? error?.message : undefined);
  }

  if (compressedFile && item.id) {
    return await generateReceipt(compressedFile, item.id, source);
  } else {
    return getErrorResp();
  }
};

const logFailedGenerate = (generatedReceipt: UploadResult) => {
  if (generatedReceipt && generatedReceipt.status === 'FAILED') {
    log(new Error(`Image upload failed.`), {
      severity: 'error',
      unhandled: true,
      logs: [
        {
          name: generatedReceipt.name,
          size: generatedReceipt.size,
          type: generatedReceipt.type,
          error: generatedReceipt.error.name,
          errorMessage: generatedReceipt.error.message,
        },
      ],
    });
  }
};

const DeliveryReceiptRoutes = () => {
  const { itemId } = useParams<{
    itemId: string;
  }>();
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [generatingState, setGeneratingState] = useState<GenerationStatus>(
    GenerationStatus.NOT_STARTED
  );

  const item = useSelector(selectItemById(itemId));

  useEffect(() => {
    if (!item && itemId) {
      dispatch(fetchItemsThunk(itemId));
    }
  }, [dispatch, item, itemId]);

  useEffect(() => {
    if (itemId) {
      dispatch(resetDeliveryReceipt());
      dispatch(resetDeliveryReceiptDocument());
      dispatch(fetchDeliveryReceiptThunk(itemId));
    }
  }, [dispatch, itemId]);

  const deliveryReceipt = useSelector(deliveryReceiptSelector());

  const generateReceipt = useCallback(
    async (file: File) => {
      if (!file || !item) {
        return null;
      }

      const source = axios.CancelToken.source();
      return await handleGenerateReceipt({
        file,
        item,
        source,
      });
    },
    [item]
  );

  const handleGeneratingReceipt = useCallback(
    async (file: File | null) => {
      if (file && itemId) {
        setGeneratingState(GenerationStatus.IN_PROGRESS);
        const generatedReceipt = await generateReceipt(file);
        if (generatedReceipt) {
          logFailedGenerate(generatedReceipt);

          if (generatedReceipt.status == 'SUCCESS') {
            dispatch(fetchDeliveryReceiptThunk(itemId)).then(() => {
              navigate('generated');
              setGeneratingState(GenerationStatus.NOT_STARTED);
            });
          } else {
            setGeneratingState(GenerationStatus.ERROR);
          }
        }
      }
    },
    [itemId, generateReceipt, dispatch, navigate]
  );

  if (!item || deliveryReceipt.isLoading) {
    return (
      <Flex grow={1}>
        <DefaultSpinner />
      </Flex>
    );
  }

  return (
    <Routes>
      <Route
        element={
          <ItemLayout
            previewPageButtonHandler={() => {
              navigate('/delivery-receipt');
            }}
            title={t('Delivery receipt')}
          />
        }
      >
        <Route
          path="/"
          element={
            <DeliveryReceiptView
              onGeneratingReceipt={handleGeneratingReceipt}
              generatingState={generatingState}
            />
          }
        />
      </Route>
      <Route path="/generated" element={<GeneratedReceiptView />} />
    </Routes>
  );
};

export default DeliveryReceiptRoutes;
